import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import range from 'lodash/range';
import isUndefined from 'lodash/isUndefined';
import RXService from '@Services/RxService';
import HeatmapHelperService from '@Pages/Heatmap/HeatmapHelperService';
import { HEATMAP_OBSERVABLE_TOPICS } from '@Pages/Heatmap/constants';

export const isClickPossible = (value) => value && value !== '0';

function Table({
  startRow,
  startCell,
  rowsToRender,
  cellsToRender,
  cellWidth,
  cellHeight,
  data,
  handleHover,
  selectedRows,
  selectedColumns,
  hoveredRow,
  hoveredColumn,
  totalRows,
  totalColumns,
}) {
  const onCellClick = ({ row, column }) => {
    // If the user clicks an empty cell nothing should happen
    if (isClickPossible(data[row][column])) {
      RXService.next(HEATMAP_OBSERVABLE_TOPICS.clickCell, {
        row,
        column,
      });
    }
  };

  const rowsViewIndexes = range(startRow, Math.min(startRow + rowsToRender, totalRows), 1);
  const cellsViewIndexes = range(startCell, Math.min(startCell + cellsToRender, totalColumns), 1);

  return (
    <div className="heatmap__matrix__inner">
      {rowsViewIndexes.map((rowViewIndex, rowVisibleIndex) =>
        cellsViewIndexes.map((cellViewIndex, cellVisibleIndex) => {
          const isRowSelected = selectedRows.indexOf(rowViewIndex) !== -1;
          const isColumnSelected = selectedColumns.indexOf(cellViewIndex) !== -1;

          return (
            !isUndefined(data[rowViewIndex]) &&
            !isUndefined(data[rowViewIndex][cellViewIndex]) && (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
              <div
                key={`${rowViewIndex}-${cellViewIndex}`}
                onClick={() => onCellClick({ row: rowViewIndex, column: cellViewIndex })}
                onMouseEnter={() => handleHover({ row: rowViewIndex, column: cellViewIndex })}
                className={classnames('heatmap__cell', {
                  'heatmap__cell--selected': isRowSelected || isColumnSelected,
                  'heatmap__cell--selected-last-row': isRowSelected && data[rowViewIndex][cellViewIndex + 1] === undefined,
                  'heatmap__cell--selected-last-column': isColumnSelected && data[rowViewIndex + 1] === undefined,
                  'heatmap__cell--hovered-row': hoveredRow && hoveredRow.index === rowViewIndex && hoveredRow.showBorder,
                  'heatmap__cell--hovered-column': hoveredColumn && hoveredColumn.index === cellViewIndex && hoveredColumn.showBorder,
                  'heatmap__cell--hovered-last-row':
                    hoveredRow &&
                    hoveredRow.index === rowViewIndex &&
                    hoveredRow.showBorder &&
                    data[rowViewIndex][cellViewIndex + 1] === undefined,
                  'heatmap__cell--hovered-last-column':
                    hoveredColumn &&
                    hoveredColumn.index === cellViewIndex &&
                    hoveredColumn.showBorder &&
                    data[rowViewIndex + 1] === undefined,
                })}
                style={{
                  width: cellWidth,
                  height: cellHeight,
                  top: cellHeight * rowVisibleIndex,
                  left: cellWidth * cellVisibleIndex,
                  borderLeft: isColumnSelected && HeatmapHelperService.getCellBorder(selectedColumns, cellViewIndex, true),
                  borderRight: isColumnSelected && HeatmapHelperService.getCellBorder(selectedColumns, cellViewIndex, false),
                  borderTop: isRowSelected && HeatmapHelperService.getCellBorder(selectedRows, rowViewIndex, true),
                  borderBottom: isRowSelected && HeatmapHelperService.getCellBorder(selectedRows, rowVisibleIndex, false),
                  backgroundColor: HeatmapHelperService.getBgColorBypX(data[rowViewIndex][cellViewIndex]),
                  color: HeatmapHelperService.getColorBypX(data[rowViewIndex][cellViewIndex]),
                  cursor: isClickPossible(data[rowViewIndex][cellViewIndex]) ? 'pointer' : 'default',
                }}>
                {HeatmapHelperService.getVisibleValueBypX(data[rowViewIndex][cellViewIndex])}
              </div>
            )
          );
        })
      )}
    </div>
  );
}

Table.propTypes = {
  startRow: PropTypes.number.isRequired,
  startCell: PropTypes.number.isRequired,
  rowsToRender: PropTypes.number.isRequired,
  cellsToRender: PropTypes.number.isRequired,
  cellWidth: PropTypes.number.isRequired,
  cellHeight: PropTypes.number.isRequired,
  data: PropTypes.object.isRequired,
  handleHover: PropTypes.func.isRequired,
  selectedRows: PropTypes.array,
  selectedColumns: PropTypes.array,
  hoveredRow: PropTypes.object,
  hoveredColumn: PropTypes.object,
  totalRows: PropTypes.number,
  totalColumns: PropTypes.number,
};

export default Table;
